import { paginationLabel, t, ui, uiError, useLanguage } from '../../i18n';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  AccountTree as BlockIcon,
  Assessment as DashboardIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  SwapHoriz as TransactionIcon
} from '@mui/icons-material';
import { getWebappApiUrl, webappFetch } from '../../utils/apiConfig';
import {
  IntegrationLedgerTransaction,
  formatBytes,
  formatRelativeTimeKo,
  formatTime,
  isValidationOperation,
  liveBusinessId,
  liveOperation,
  livePayloadSizeBytes,
  operationDisplayLabel,
  shortBusinessId,
  shortHash,
  sourceOrgOf,
  targetOrgOf
} from '../../domain/integrationLedger';
import {
  DashboardMetricCard,
  DetailField,
  IndexStatusChip,
  IntegrationTransactionDetailDialog,
  LedgerStatusChip
} from './IntegrationLedgerDialogs';

type PartnerKey = 'EINS' | 'KAIST' | 'KULS';
type LedgerDirection = 'source' | 'target' | 'involved';
type PartnerLedgerTx = IntegrationLedgerTransaction & { matches_filter?: boolean };

type PartnerConfig = {
  key: PartnerKey;
  title: string;
  dashboardTitle: string;
  transactionsTitle: string;
  blocksTitle: string;
  roleText: string;
  org: PartnerKey;
  direction: LedgerDirection;
  flowLabel: string;
  recordLabel: string;
  primaryColor: string;
  secondaryColor: string;
  operations: string[];
  filterOperations?: string[];
  operationalOperations: string[];
  validationOperations: string[];
};

type UserInfo = {
  username: string;
  displayName: string;
  role: string;
  orgName: string;
  orgIcon: string;
  loginTime: string;
};

type PaginationState = {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
};

type SummaryResponse = {
  summary: Record<string, any>;
  networkStats: Record<string, any>;
  operationBreakdown: Array<{ operation: string; count: number }>;
  recentTransactions: PartnerLedgerTx[];
};

type PartnerBlockGroup = {
  blockNumber: string;
  rows: PartnerLedgerTx[];
  representative: PartnerLedgerTx;
  visibleCount: number;
};

const ITEMS_PER_TRANSACTION_PAGE = 20;
const ITEMS_PER_BLOCK_PAGE = 12;
const LEDGER_FROM_BLOCK = 0;
const EMPTY_PAGINATION: PaginationState = {
  currentPage: 1,
  totalItems: 0,
  itemsPerPage: ITEMS_PER_TRANSACTION_PAGE,
  totalPages: 1,
  hasNext: false,
  hasPrev: false
};

const PARTNER_CONFIGS: Record<PartnerKey, PartnerConfig> = {
  EINS: {
    key: 'EINS',
    title: 'eINS',
    dashboardTitle: 'eINS Dispatch Ledger Dashboard',
    transactionsTitle: 'eINS Transaction Tracking',
    blocksTitle: 'eINS Block Explorer',
    roleText: 'Shows dispatch optimization result records submitted by eINS to KULS.',
    org: 'EINS',
    direction: 'source',
    flowLabel: 'Source: EINS',
    recordLabel: 'EINS Source Records',
    primaryColor: '#1976d2',
    secondaryColor: '#009688',
    operations: ['Daily_Opt', 'Emergency_Opt', 'Validation_Opt'],
    filterOperations: ['Daily_Opt', 'Emergency_Opt', 'Validation_Opt'],
    operationalOperations: ['Daily_Opt', 'Emergency_Opt'],
    validationOperations: ['Validation_Opt']
  },
  KAIST: {
    key: 'KAIST',
    title: 'KAIST',
    dashboardTitle: 'KAIST Yard Optimization Ledger Dashboard',
    transactionsTitle: 'KAIST Transaction Tracking',
    blocksTitle: 'KAIST Block Explorer',
    roleText: 'Shows yard optimization result records submitted by KAIST to KULS.',
    org: 'KAIST',
    direction: 'source',
    flowLabel: 'Source: KAIST',
    recordLabel: 'KAIST Source Records',
    primaryColor: '#5e35b1',
    secondaryColor: '#1976d2',
    operations: ['assignment', 'retrieval', 'relocation'],
    operationalOperations: ['assignment', 'retrieval', 'relocation'],
    validationOperations: []
  },
  KULS: {
    key: 'KULS',
    title: 'KULS',
    dashboardTitle: 'KULS Partner Result Dashboard',
    transactionsTitle: 'KULS Transaction Tracking',
    blocksTitle: 'KULS Block Explorer',
    roleText: 'Shows partner result records received by KULS from eINS and KAIST.',
    org: 'KULS',
    direction: 'target',
    flowLabel: 'Target: KULS',
    recordLabel: 'Inbound Partner Records',
    primaryColor: '#2e7d32',
    secondaryColor: '#0288d1',
    operations: ['Daily_Opt', 'Emergency_Opt', 'Validation_Opt', 'assignment', 'retrieval', 'relocation'],
    filterOperations: ['Daily_Opt', 'Emergency_Opt', 'Validation_Opt', 'assignment', 'retrieval', 'relocation'],
    operationalOperations: ['Daily_Opt', 'Emergency_Opt', 'assignment', 'retrieval', 'relocation'],
    validationOperations: ['Validation_Opt']
  }
};

function partnerConfig(partner: PartnerKey) {
  return PARTNER_CONFIGS[partner];
}

function toNumber(value: unknown) {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function blockNumberValue(value: unknown) {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function sortRows(rows: PartnerLedgerTx[]) {
  return rows.slice().sort((a, b) => (
    blockNumberValue(b.block_number) - blockNumberValue(a.block_number)
    || (b.transaction_index || 0) - (a.transaction_index || 0)
  ));
}

function partnerQueryParams(config: PartnerConfig, extra: Record<string, string | number | boolean>) {
  const params = new URLSearchParams({
    fromBlock: String(LEDGER_FROM_BLOCK),
    org: config.org,
    direction: config.direction,
    businessOnly: 'true'
  });
  Object.entries(extra).forEach(([key, value]) => {
    params.set(key, String(value));
  });
  return params;
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await webappFetch(url);
  const body = await response.json();
  if (!response.ok || !body.success) {
    throw new Error(body.message || body.error || `HTTP ${response.status}`);
  }
  return body.data as T;
}

function operationCount(summary: SummaryResponse | null, operation: string) {
  return toNumber(summary?.operationBreakdown?.find((item) => item.operation === operation)?.count);
}

function operationTotal(summary: SummaryResponse | null, operations: string[]) {
  return operations.reduce((sum, operation) => sum + operationCount(summary, operation), 0);
}

function operationChipColor(operation: string, active = false): 'default' | 'success' | 'warning' {
  if (!active) return 'default';
  return isValidationOperation(operation) ? 'warning' : 'success';
}

function OperationBadge({ operation }: { operation: string }) {
  if (isValidationOperation(operation)) {
    return (
      <Chip
        size="small"
        color="warning"
        variant="outlined"
        label={ui(operationDisplayLabel(operation))}
        title={ui(operation)}
      />
    );
  }

  return <Typography variant="body2">{operation || '-'}</Typography>;
}

function isOperationalTx(tx: PartnerLedgerTx, config: PartnerConfig) {
  return config.operationalOperations.includes(liveOperation(tx));
}

function OperationBreakdownGroup({
  title,
  subtitle,
  operations,
  summary,
  color,
  softColor
}: {
  title: string;
  subtitle: string;
  operations: string[];
  summary: SummaryResponse | null;
  color: string;
  softColor: string;
}) {
  const counts = operations.map((operation) => ({
    operation,
    count: operationCount(summary, operation)
  }));
  const total = counts.reduce((sum, item) => sum + item.count, 0);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 1,
        backgroundColor: softColor,
        borderColor: 'rgba(15, 23, 42, 0.08)'
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5} sx={{ mb: 1.25 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={800}>
            {ui(title)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {ui(subtitle)}
          </Typography>
        </Box>
        <Chip size="small" label={total.toLocaleString()} sx={{ bgcolor: color, color: '#fff', fontWeight: 800 }} />
      </Stack>

      <Stack spacing={1.1}>
        {counts.map(({ operation, count }) => {
          const width = total > 0 ? Math.max(6, Math.round((count / total) * 100)) : 0;
          return (
            <Box key={operation}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography variant="body2" fontWeight={700}>
                  {ui(operationDisplayLabel(operation))}
                </Typography>
                <Typography variant="body2" fontWeight={900}>
                  {count.toLocaleString()}
                </Typography>
              </Stack>
              <Box
                sx={{
                  mt: 0.65,
                  height: 7,
                  borderRadius: 1,
                  backgroundColor: 'rgba(15, 23, 42, 0.08)',
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    width: `${width}%`,
                    height: '100%',
                    borderRadius: 1,
                    backgroundColor: color,
                    transition: 'width 300ms ease'
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}

function SplitMetricValue({
  leftLabel,
  leftValue,
  rightLabel,
  rightValue
}: {
  leftLabel: string;
  leftValue: number;
  rightLabel: string;
  rightValue: number;
}) {
  return (
    <Box
      component="span"
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 1.25,
        width: '100%',
        maxWidth: 210
      }}
    >
      {[
        [leftLabel, leftValue],
        [rightLabel, rightValue]
      ].map(([label, value]) => (
        <Box
          component="span"
          key={label}
          sx={{
            display: 'block',
            minWidth: 0,
            pr: 1,
            '&:first-of-type': {
              borderRight: '1px solid rgba(255, 255, 255, 0.45)'
            }
          }}
        >
          <Typography component="span" sx={{ display: 'block', fontSize: 30, fontWeight: 900, lineHeight: 1 }}>
            {Number(value).toLocaleString()}
          </Typography>
          <Typography component="span" sx={{ display: 'block', fontSize: 11, fontWeight: 800, opacity: 0.82, mt: 0.4 }}>
            {ui(label)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function CompactFlowMetricValue({ value }: { value: string }) {
  return (
    <Typography
      component="span"
      sx={{
        display: 'block',
        fontSize: { xs: 27, sm: 29, lg: 30 },
        fontWeight: 900,
        lineHeight: 1.05,
        whiteSpace: 'nowrap',
        letterSpacing: 0
      }}
    >
      {value}
    </Typography>
  );
}

function LatestInboundCaption({ tx }: { tx: PartnerLedgerTx | null }) {
  if (!tx) return <>{t("No inbound record indexed yet")}</>;

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.35 }}>
      <Chip
        size="small"
        label={liveOperation(tx)}
        sx={{
          height: 22,
          fontWeight: 900,
          color: '#fff',
          bgcolor: '#0288d1',
          '& .MuiChip-label': { px: 0.9 }
        }}
      />
      <Typography component="span" variant="caption" sx={{ fontWeight: 800, color: 'text.primary' }}>{t("Block #")}{tx.block_number}
      </Typography>
    </Stack>
  );
}

function RecentRecordsList({
  rows,
  highlightBlock,
  config
}: {
  rows: PartnerLedgerTx[];
  highlightBlock: string | null;
  config: PartnerConfig;
}) {
  const gridTemplateColumns = {
    xs: 'minmax(110px, 1fr) 52px minmax(70px, 0.7fr) 124px',
    md: 'minmax(180px, 1.25fr) 64px minmax(106px, 0.75fr) 160px'
  };

  const cellSx = {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  };

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          gap: 1.5,
          px: 1.5,
          py: 1,
          bgcolor: '#f5f5f5',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        }}
      >
        {['Time', 'Block', counterpartyColumnLabel(config), 'Operation'].map((label) => (
          <Typography key={label} variant="body2" fontWeight={800} sx={cellSx}>
            {ui(label)}
          </Typography>
        ))}
      </Box>

      {rows.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">{t("No operational records indexed.")}</Typography>
        </Box>
      ) : rows.map((tx) => (
        <Box
          key={tx.transaction_id}
          sx={{
            display: 'grid',
            gridTemplateColumns,
            gap: 1.5,
            alignItems: 'center',
            px: 1.5,
            py: 0.9,
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            ...(String(tx.block_number) === highlightBlock ? {
              animation: 'partnerDashboardNewRow 4s ease-out',
              '@keyframes partnerDashboardNewRow': {
                '0%': { backgroundColor: 'rgba(46, 125, 50, 0.24)' },
                '100%': { backgroundColor: 'transparent' }
              }
            } : {})
          }}
        >
          <Typography variant="body2" title={formatTime(tx.block_created_at)} sx={cellSx}>{formatTime(tx.block_created_at)}</Typography>
          <Typography variant="body2" sx={cellSx}>#{tx.block_number}</Typography>
          <Typography variant="body2" title={flowOf(tx)} sx={cellSx}>{counterpartyOf(tx, config)}</Typography>
          <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
            <OperationBadge operation={liveOperation(tx)} />
          </Box>
        </Box>
      ))}
    </Paper>
  );
}

function flowOf(tx: PartnerLedgerTx) {
  return `${sourceOrgOf(tx) || '-'} → ${targetOrgOf(tx) || '-'}`;
}

function counterpartyColumnLabel(config: PartnerConfig) {
  return config.key === 'KULS' ? 'Source' : 'Receiver';
}

function counterpartyOf(tx: PartnerLedgerTx, config: PartnerConfig) {
  return config.key === 'KULS' ? sourceOrgOf(tx) || '-' : targetOrgOf(tx) || '-';
}

function isPartnerVisibleTx(tx: PartnerLedgerTx, config: PartnerConfig) {
  return sourceOrgOf(tx) === config.org || targetOrgOf(tx) === config.org;
}

function buildBlockGroups(rows: PartnerLedgerTx[]): PartnerBlockGroup[] {
  const grouped = new Map<string, PartnerLedgerTx[]>();
  rows.forEach((tx) => {
    const key = String(tx.block_number || '-');
    grouped.set(key, [...(grouped.get(key) || []), tx]);
  });

  return Array.from(grouped.entries())
    .map(([blockNumber, blockRows]) => {
      const sorted = blockRows.slice().sort((a, b) => (a.transaction_index || 0) - (b.transaction_index || 0));
      const matchingRows = sorted.filter((tx) => tx.matches_filter !== false);
      return {
        blockNumber,
        rows: sorted,
        representative: matchingRows[0] || sorted[0],
        visibleCount: matchingRows.length || sorted.length
      };
    })
    .sort((a, b) => blockNumberValue(b.blockNumber) - blockNumberValue(a.blockNumber));
}

function OperationFilter({
  config,
  value,
  onChange
}: {
  config: PartnerConfig;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Chip
        clickable
        color={value === 'ALL' ? 'success' : 'default'}
        variant={value === 'ALL' ? 'filled' : 'outlined'}
        label={t("All")}
        onClick={() => onChange('ALL')}
      />
      {(config.filterOperations || config.operations).map((operation) => (
        <Chip
          key={operation}
          clickable
          color={operationChipColor(operation, value === operation)}
          variant={value === operation ? 'filled' : 'outlined'}
          label={ui(operationDisplayLabel(operation))}
          onClick={() => onChange(operation)}
        />
      ))}
    </Stack>
  );
}

export function PartnerLedgerDashboard({ partner, user }: { partner: PartnerKey; user: UserInfo }) {
  const config = partnerConfig(partner);
  const [data, setData] = useState<SummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [highlightBlock, setHighlightBlock] = useState<string | null>(null);
  const latestBlockRef = useRef<string | null>(null);

  const loadData = useCallback(async ({ initial = false }: { initial?: boolean } = {}) => {
    try {
      if (initial) setLoading(true);
      setError(null);
      const params = partnerQueryParams(config, { recentLimit: config.key === 'KULS' ? 50 : 20 });
      const nextData = await fetchJson<SummaryResponse>(`${getWebappApiUrl()}/api/blockchain/integration-prototype/summary?${params.toString()}`);
      const latestOperational = (nextData.recentTransactions || []).find((tx) => isOperationalTx(tx, config));
      const latestBlock = String(latestOperational?.block_number || '');
      if (latestBlockRef.current && latestBlock && latestBlock !== latestBlockRef.current) {
        setHighlightBlock(latestBlock);
        window.setTimeout(() => setHighlightBlock(null), 4000);
      }
      latestBlockRef.current = latestBlock || latestBlockRef.current;
      setData(nextData);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load partner ledger summary');
    } finally {
      if (initial) setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    loadData({ initial: true });
    const timer = window.setInterval(() => loadData(), 2000);
    return () => window.clearInterval(timer);
  }, [loadData]);

  const summary = data?.summary || {};
  const networkStats = data?.networkStats || {};
  const recentRows = data?.recentTransactions || [];
  const allOperationalRecentRows = recentRows.filter((tx) => isOperationalTx(tx, config));
  const operationalRecentRows = allOperationalRecentRows.slice(0, 5);
  const kulsEinsRecentRows = allOperationalRecentRows.filter((tx) => sourceOrgOf(tx) === 'EINS').slice(0, 6);
  const kulsKaistRecentRows = allOperationalRecentRows.filter((tx) => sourceOrgOf(tx) === 'KAIST').slice(0, 6);
  const latestRow = operationalRecentRows[0] || null;
  const businessRecords = toNumber(summary.business_records);
  const indexedBlocks = toNumber(summary.business_blocks);
  const operationalRecords = operationTotal(data, config.operationalOperations);
  const einsToKulsRecords = toNumber(summary.eins_to_kuls_records);
  const kaistToKulsRecords = toNumber(summary.kaist_to_kuls_records);
  const hashReady = toNumber(summary.hash_ref_records);
  const v1Records = toNumber(summary.v1_records);
  const legacyCompatibleRecords = Math.max(0, businessRecords - v1Records);
  const totalBlocks = toNumber(networkStats.total_blocks);
  const totalTransactions = toNumber(networkStats.total_transactions);
  const latestNetworkBlockNumber = toNumber(networkStats.latest_block_number ?? networkStats.latestBlockNumber ?? Math.max(totalBlocks - 1, 0));
  const metricHighlight = Boolean(highlightBlock);
  const isKulsDashboard = config.key === 'KULS';

  return (
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: config.primaryColor, fontWeight: 'bold' }}>
          <DashboardIcon sx={{ fontSize: 34 }} />
          {ui(config.dashboardTitle)}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          {ui(config.roleText)}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{uiError(error)}</Alert>}

      {loading && !data ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t("Loading partner ledger summary.")}</Typography>
        </Stack>
      ) : (
        <Stack spacing={3}>
          {isKulsDashboard ? (
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} lg={3}>
                <DashboardMetricCard
                  label={t("Network Indexed")}
                  value={<SplitMetricValue leftLabel="Blocks" leftValue={totalBlocks} rightLabel="Tx" rightValue={totalTransactions} />}
                  caption={totalBlocks > 0 ? t("Indexed blocks, #0 to #{0}", [latestNetworkBlockNumber.toLocaleString()]) : t("No indexed block yet")}
                  color={config.primaryColor}
                  gradient={`linear-gradient(135deg, ${config.primaryColor}, ${config.secondaryColor})`}
                  highlight={metricHighlight}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <DashboardMetricCard
                  label={t("KULS Receiver Blocks")}
                  value={indexedBlocks.toLocaleString()}
                  caption={t("Distinct blocks containing KULS inbound records")}
                  color="#2e7d32"
                  highlight={metricHighlight}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <DashboardMetricCard
                  label={t("KULS Receiver Transactions")}
                  value={businessRecords.toLocaleString()}
                  caption={t("Transactions where receiver/target is KULS")}
                  color={config.primaryColor}
                  icon={<TransactionIcon />}
                  highlight={metricHighlight}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <DashboardMetricCard
                  label={t("Latest KULS Inbound")}
                  value={<CompactFlowMetricValue value={latestRow ? sourceOrgOf(latestRow) || '-' : '-'} />}
                  caption={<LatestInboundCaption tx={latestRow} />}
                  color="#0288d1"
                  highlight={metricHighlight}
                />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} lg={3}>
                <DashboardMetricCard
                  label={t("Network Indexed")}
                  value={<SplitMetricValue leftLabel="Blocks" leftValue={totalBlocks} rightLabel="Tx" rightValue={totalTransactions} />}
                  caption={totalBlocks > 0 ? t("Indexed blocks, #0 to #{0}", [latestNetworkBlockNumber.toLocaleString()]) : t("No indexed block yet")}
                  color={config.primaryColor}
                  gradient={`linear-gradient(135deg, ${config.primaryColor}, ${config.secondaryColor})`}
                  highlight={metricHighlight}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <DashboardMetricCard
                  label={t("Submitted Blocks")}
                  value={indexedBlocks.toLocaleString()}
                  caption={t("Distinct blocks containing {0} submitted records", [config.title])}
                  color="#2e7d32"
                  highlight={metricHighlight}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <DashboardMetricCard
                  label={t("Submitted Transactions")}
                  value={businessRecords.toLocaleString()}
                  caption={t("Transactions submitted by {0}", [config.title])}
                  color={config.primaryColor}
                  icon={<TransactionIcon />}
                  highlight={metricHighlight}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <DashboardMetricCard
                  label={t("Latest Submitted")}
                  value={<CompactFlowMetricValue value={latestRow ? liveOperation(latestRow) : '-'} />}
                  caption={latestRow ? t("To {0} / Block #{1}", [targetOrgOf(latestRow) || '-', latestRow.block_number]) : t("No submitted record indexed yet")}
                  color="#0288d1"
                  highlight={metricHighlight}
                />
              </Grid>
            </Grid>
          )}

          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={6}>
              <Stack spacing={2.5} sx={{ height: '100%' }}>
                <Card sx={{ flex: config.key === 'KULS' ? 'initial' : 1 }}>
                  <CardContent>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 1.5 }}>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">{t("Operation Breakdown")}</Typography>
                        <Typography variant="caption" color="text.secondary">{t("Operational records are grouped by business operation.")}</Typography>
                      </Box>
                      <Chip size="small" variant="outlined" label={t("{0} operational", [operationalRecords.toLocaleString()])} />
                    </Stack>
                    <Stack spacing={1.5}>
                      <OperationBreakdownGroup
                        title={t("Operational Records")}
                        subtitle={t("Included in operation monitoring")}
                        operations={config.operationalOperations}
                        summary={data}
                        color={config.primaryColor}
                        softColor="rgba(15, 23, 42, 0.02)"
                      />
                    </Stack>
                  </CardContent>
                </Card>

                {config.key === 'KULS' && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>{t("Source Breakdown")}</Typography>
                      <Stack spacing={1.5}>
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">{t("eINS → KULS")}</Typography>
                          <Typography variant="h5" fontWeight="bold">{einsToKulsRecords.toLocaleString()}</Typography>
                          <Typography variant="caption" color="text.secondary">{t("Dispatch optimization results")}</Typography>
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">{t("KAIST → KULS")}</Typography>
                          <Typography variant="h5" fontWeight="bold">{kaistToKulsRecords.toLocaleString()}</Typography>
                          <Typography variant="caption" color="text.secondary">{t("Yard optimization results")}</Typography>
                        </Paper>
                      </Stack>
                    </CardContent>
                  </Card>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>{t("Recent Records")}</Typography>
                  {config.key === 'KULS' ? (
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.75 }}>{t("eINS → KULS")}</Typography>
                        <RecentRecordsList rows={kulsEinsRecentRows} highlightBlock={highlightBlock} config={config} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.75 }}>{t("KAIST → KULS")}</Typography>
                        <RecentRecordsList rows={kulsKaistRecentRows} highlightBlock={highlightBlock} config={config} />
                      </Box>
                    </Stack>
                  ) : (
                    <RecentRecordsList rows={operationalRecentRows} highlightBlock={highlightBlock} config={config} />
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">{t("Evidence Model")}</Typography>
                      <Typography variant="body2" color="text.secondary">{t("v1 records support payload hash/ref recheck. Legacy records remain committed and indexed without payloadRef metadata.")}</Typography>
                    </Box>
                    <Chip size="small" color="primary" variant="outlined" label={t("Ledger evidence scope")} />
                  </Stack>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={4}>
                      <Paper variant="outlined" sx={{ p: 1.5, height: '100%', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">{t("v1 hash-ref records")}</Typography>
                        <Typography variant="h5" fontWeight="bold">{hashReady.toLocaleString()}</Typography>
                        <Typography variant="caption" color="text.secondary">{t("Payload hash and payload reference are available.")}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper variant="outlined" sx={{ p: 1.5, height: '100%', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">{t("Legacy / non-v1 records")}</Typography>
                        <Typography variant="h5" fontWeight="bold">{legacyCompatibleRecords.toLocaleString()}</Typography>
                        <Typography variant="caption" color="text.secondary">{t("Committed ledger records without v1 payloadRef recheck metadata.")}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper variant="outlined" sx={{ p: 1.5, height: '100%', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">{t("Payload recheck")}</Typography>
                        <Typography variant="h5" fontWeight="bold">{hashReady > 0 ? t("Available") : t("Not yet")}</Typography>
                        <Typography variant="caption" color="text.secondary">{t("Available when a record has both payloadHash and payloadRef.")}</Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                  <Typography variant="body2" color="text.secondary">{t("This dashboard uses the WebApp PostgreSQL ledger index and refreshes every 2 seconds.")}</Typography>
                  <Typography variant="body2" color="text.secondary">{t("Network index:")}{" "}{totalBlocks.toLocaleString()}{" "}{t("blocks /")}{" "}{totalTransactions.toLocaleString()}{" "}{t("transactions. Latest scoped activity:")}{" "}{ui(formatRelativeTimeKo(latestRow?.block_created_at))}.
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      )}
    </Box>
  );
}

export function PartnerLedgerTransactions({ partner, user }: { partner: PartnerKey; user: UserInfo }) {
  useLanguage();
  const config = partnerConfig(partner);
  const [operationFilter, setOperationFilter] = useState('ALL');
  const [query, setQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rows, setRows] = useState<PartnerLedgerTx[]>([]);
  const [pagination, setPagination] = useState<PaginationState>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTx, setSelectedTx] = useState<PartnerLedgerTx | null>(null);
  const [newTxIds, setNewTxIds] = useState<Set<string>>(new Set());
  const knownTxIdsRef = useRef<Set<string>>(new Set());
  const initializedRef = useRef(false);

  const loadPage = useCallback(async ({
    initial = false,
    animateNew = false
  }: {
    initial?: boolean;
    animateNew?: boolean;
  } = {}) => {
    try {
      if (initial) setLoading(true);
      setError(null);
      const params = partnerQueryParams(config, {
        page: currentPage,
        limit: ITEMS_PER_TRANSACTION_PAGE,
        operation: operationFilter,
        query
      });
      const data = await fetchJson<{ transactions: PartnerLedgerTx[]; pagination: PaginationState }>(
        `${getWebappApiUrl()}/api/blockchain/integration-prototype/transactions-page?${params.toString()}`
      );
      const sorted = sortRows(data.transactions || []);
      const nextIds = new Set(sorted.map((tx) => tx.transaction_id).filter(Boolean));
      if (!animateNew) {
        setNewTxIds(new Set());
      }
      if (initializedRef.current && currentPage === 1 && animateNew) {
        const newIds = Array.from(nextIds).filter((id) => !knownTxIdsRef.current.has(id));
        if (newIds.length) {
          setNewTxIds((previous) => {
            const merged = new Set(previous);
            newIds.forEach((id) => merged.add(id));
            return merged;
          });
          window.setTimeout(() => {
            setNewTxIds((previous) => {
              const next = new Set(previous);
              newIds.forEach((id) => next.delete(id));
              return next;
            });
          }, 4000);
        }
      }
      knownTxIdsRef.current = nextIds;
      initializedRef.current = true;
      setRows(sorted);
      setPagination(data.pagination || EMPTY_PAGINATION);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load partner transactions');
    } finally {
      if (initial) setLoading(false);
    }
  }, [config, currentPage, operationFilter, query]);

  useEffect(() => {
    loadPage({ initial: true });
    const timer = window.setInterval(() => loadPage({ animateNew: true }), 2000);
    return () => window.clearInterval(timer);
  }, [loadPage]);

  const resetPage = (action: () => void) => {
    setNewTxIds(new Set());
    action();
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, pagination.totalPages || 1);
  const newRowSx = {
    animation: 'partnerTxNewRowFlash 4s ease-out',
    '@keyframes partnerTxNewRowFlash': {
      '0%': { backgroundColor: 'rgba(46, 125, 50, 0.24)' },
      '18%': { backgroundColor: 'rgba(46, 125, 50, 0.16)' },
      '100%': { backgroundColor: 'transparent' }
    }
  };

  return (
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: config.primaryColor, fontWeight: 'bold' }}>
          <TransactionIcon sx={{ fontSize: 34 }} />
          {ui(config.transactionsTitle)}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          {ui(config.roleText)}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{uiError(error)}</Alert>}

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t("Operation")}</Typography>
              <OperationFilter config={config} value={operationFilter} onChange={(value) => resetPage(() => setOperationFilter(value))} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t("Scope")}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip color="primary" label={ui(config.flowLabel)} />
                <Chip variant="outlined" label={config.direction === 'source' ? t("Submitted records") : t("Received records")} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            fullWidth
            size="small"
            value={query}
            onChange={(event) => resetPage(() => setQuery(event.target.value))}
            placeholder={t("Search Tx ID, business ID, payload hash, organization, or operation")}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} /> }}
          />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => loadPage({ animateNew: false })} sx={{ whiteSpace: 'nowrap' }}>{t("Refresh")}</Button>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          <Chip size="small" color="primary" variant="outlined" label={t("{0} records", [pagination.totalItems])} />
          <Chip size="small" variant="outlined" label={t("{0} shown", [rows.length])} />
          <Chip size="small" variant="outlined" label={t("Server-paginated PostgreSQL index")} />
        </Stack>
      </Paper>

      <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 1040 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>{t("Time")}</strong></TableCell>
              <TableCell><strong>{t("Block")}</strong></TableCell>
              <TableCell><strong>{t("Tx ID")}</strong></TableCell>
              <TableCell><strong>{ui(counterpartyColumnLabel(config))}</strong></TableCell>
              <TableCell><strong>{t("Business ID")}</strong></TableCell>
              <TableCell><strong>{t("Operation")}</strong></TableCell>
              <TableCell><strong>{t("Payload")}</strong></TableCell>
              <TableCell><strong>{t("Status")}</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={26} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t("Syncing partner transactions.")}</Typography>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>{t("No transactions match the current filters.")}</TableCell>
              </TableRow>
            ) : rows.map((tx) => (
              <TableRow
                hover
                key={tx.transaction_id}
                onClick={() => setSelectedTx(tx)}
                sx={{ cursor: 'pointer', ...(newTxIds.has(tx.transaction_id) ? newRowSx : {}) }}
              >
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTime(tx.block_created_at)}</TableCell>
                <TableCell>#{tx.block_number}</TableCell>
                <TableCell title={tx.transaction_id} sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{shortHash(tx.transaction_id, 10, 8)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }} title={flowOf(tx)}>{counterpartyOf(tx, config)}</TableCell>
                <TableCell title={liveBusinessId(tx)} sx={{ fontFamily: 'monospace', maxWidth: 230, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {shortBusinessId(liveBusinessId(tx))}
                </TableCell>
                <TableCell><OperationBadge operation={liveOperation(tx)} /></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatBytes(livePayloadSizeBytes(tx))}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    <LedgerStatusChip tx={tx} />
                    <IndexStatusChip tx={tx} />
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination getItemAriaLabel={paginationLabel} aria-label={t("Pagination")} page={currentPage} count={totalPages} color="primary" onChange={(_, page) => setCurrentPage(page)} />
        </Stack>
      )}

      <IntegrationTransactionDetailDialog tx={selectedTx} onClose={() => setSelectedTx(null)} />
    </Box>
  );
}

function PartnerBlockDetailDialog({
  config,
  block,
  onClose,
  onSelectTx
}: {
  config: PartnerConfig;
  block: PartnerBlockGroup | null;
  onClose: () => void;
  onSelectTx: (tx: PartnerLedgerTx) => void;
}) {
  const representative = block?.representative || null;
  const totalTxCount = representative?.transaction_count || block?.rows.length || 0;

  return (
    <Box>
      <DialogShell
        open={Boolean(block)}
        onClose={onClose}
        title={t("Block Detail")}
      >
        {block && representative && (
          <Stack spacing={2}>
            <Alert severity="info">{t("This view shows every indexed transaction in the selected block.")}{" "}{ui(config.title)}{" "}{t("can open records related to")}{" "}{ui(config.title)}{t("; other organization records are shown as block metadata only.")}</Alert>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Block")} value={`#${block.blockNumber}`} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Created At")} value={formatTime(representative.block_created_at)} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Transactions in Block")} value={`${totalTxCount}`} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("{0} Visible Tx", [config.title])} value={`${block.visibleCount}`} /></Grid>
              <Grid item xs={12}><DetailField label={t("Block Hash")} value={representative.block_hash || '-'} mono /></Grid>
              <Grid item xs={12}><DetailField label={t("Data Hash")} value={representative.data_hash || '-'} mono /></Grid>
              <Grid item xs={12}><DetailField label={t("Previous Hash")} value={representative.previous_hash || '-'} mono /></Grid>
            </Grid>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ width: 42 }}><strong>#</strong></TableCell>
                    <TableCell sx={{ width: 100 }}><strong>{ui(counterpartyColumnLabel(config))}</strong></TableCell>
                    <TableCell><strong>{t("Business ID")}</strong></TableCell>
                    <TableCell sx={{ width: 130 }}><strong>{t("Operation")}</strong></TableCell>
                    <TableCell sx={{ width: 140 }}><strong>{t("Tx ID")}</strong></TableCell>
                    <TableCell sx={{ width: 190 }}><strong>{t("Access")}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {block.rows.map((tx) => {
                    const canOpen = isPartnerVisibleTx(tx, config);
                    return (
                      <TableRow
                        hover={canOpen}
                        key={tx.transaction_id}
                        onClick={() => canOpen && onSelectTx(tx)}
                        sx={{ cursor: canOpen ? 'pointer' : 'default', opacity: canOpen ? 1 : 0.7 }}
                      >
                        <TableCell>{tx.transaction_index}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }} title={flowOf(tx)}>{counterpartyOf(tx, config)}</TableCell>
                        <TableCell title={liveBusinessId(tx)} sx={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {shortBusinessId(liveBusinessId(tx))}
                        </TableCell>
                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <OperationBadge operation={liveOperation(tx)} />
                        </TableCell>
                        <TableCell title={tx.transaction_id} sx={{ fontFamily: 'monospace' }}>{shortHash(tx.transaction_id, 8, 6)}</TableCell>
                        <TableCell>
                          {canOpen ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              <LedgerStatusChip tx={tx} />
                              <IndexStatusChip tx={tx} />
                            </Stack>
                          ) : (
                            <Chip size="small" variant="outlined" label={t("Other organization metadata")} />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}
      </DialogShell>
    </Box>
  );
}

function DialogShell({
  open,
  title,
  children,
  onClose
}: {
  open: boolean;
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{ui(title)}</DialogTitle>
      <DialogContent dividers>{children}</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Close")}</Button>
      </DialogActions>
    </Dialog>
  );
}

export function PartnerLedgerBlocks({ partner, user }: { partner: PartnerKey; user: UserInfo }) {
  useLanguage();
  const config = partnerConfig(partner);
  const [operationFilter, setOperationFilter] = useState('ALL');
  const [query, setQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rows, setRows] = useState<PartnerLedgerTx[]>([]);
  const [pagination, setPagination] = useState<PaginationState>({ ...EMPTY_PAGINATION, itemsPerPage: ITEMS_PER_BLOCK_PAGE });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBlockNumber, setSelectedBlockNumber] = useState<string | null>(null);
  const [selectedTx, setSelectedTx] = useState<PartnerLedgerTx | null>(null);
  const [newBlockNumbers, setNewBlockNumbers] = useState<Set<string>>(new Set());
  const knownBlocksRef = useRef<Set<string>>(new Set());
  const initializedRef = useRef(false);

  const blockGroups = useMemo(() => buildBlockGroups(rows), [rows]);
  const selectedBlock = selectedBlockNumber ? blockGroups.find((block) => block.blockNumber === selectedBlockNumber) || null : null;

  const loadPage = useCallback(async ({
    initial = false,
    animateNew = false
  }: {
    initial?: boolean;
    animateNew?: boolean;
  } = {}) => {
    try {
      if (initial) setLoading(true);
      setError(null);
      const params = partnerQueryParams(config, {
        page: currentPage,
        limit: ITEMS_PER_BLOCK_PAGE,
        operation: operationFilter,
        query,
        includeBlockTransactions: 'all'
      });
      const data = await fetchJson<{ transactions: PartnerLedgerTx[]; pagination: PaginationState }>(
        `${getWebappApiUrl()}/api/blockchain/integration-prototype/blocks-page?${params.toString()}`
      );
      const sorted = sortRows(data.transactions || []);
      const nextBlocks = new Set(sorted.filter((tx) => tx.matches_filter !== false).map((tx) => String(tx.block_number)).filter(Boolean));
      if (!animateNew) {
        setNewBlockNumbers(new Set());
      }
      if (initializedRef.current && currentPage === 1 && animateNew) {
        const newBlocks = Array.from(nextBlocks).filter((blockNumber) => !knownBlocksRef.current.has(blockNumber));
        if (newBlocks.length) {
          setNewBlockNumbers((previous) => {
            const merged = new Set(previous);
            newBlocks.forEach((blockNumber) => merged.add(blockNumber));
            return merged;
          });
          window.setTimeout(() => {
            setNewBlockNumbers((previous) => {
              const next = new Set(previous);
              newBlocks.forEach((blockNumber) => next.delete(blockNumber));
              return next;
            });
          }, 4000);
        }
      }
      knownBlocksRef.current = nextBlocks;
      initializedRef.current = true;
      setRows(sorted);
      setPagination(data.pagination || { ...EMPTY_PAGINATION, itemsPerPage: ITEMS_PER_BLOCK_PAGE });
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load partner blocks');
    } finally {
      if (initial) setLoading(false);
    }
  }, [config, currentPage, operationFilter, query]);

  useEffect(() => {
    loadPage({ initial: true });
    const timer = window.setInterval(() => loadPage({ animateNew: true }), 2000);
    return () => window.clearInterval(timer);
  }, [loadPage]);

  const resetPage = (action: () => void) => {
    setNewBlockNumbers(new Set());
    action();
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, pagination.totalPages || 1);
  const newBlockSx = {
    animation: 'partnerBlockNewRowFlash 4s ease-out',
    '@keyframes partnerBlockNewRowFlash': {
      '0%': { backgroundColor: 'rgba(46, 125, 50, 0.24)' },
      '18%': { backgroundColor: 'rgba(46, 125, 50, 0.16)' },
      '100%': { backgroundColor: 'transparent' }
    }
  };

  return (
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: config.primaryColor, fontWeight: 'bold' }}>
          <BlockIcon sx={{ fontSize: 34 }} />
          {ui(config.blocksTitle)}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          {ui(config.roleText)}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{uiError(error)}</Alert>}

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t("Operation")}</Typography>
              <OperationFilter config={config} value={operationFilter} onChange={(value) => resetPage(() => setOperationFilter(value))} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t("Scope")}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip color="primary" label={ui(config.flowLabel)} />
                <Chip variant="outlined" label={t("Blocks containing scoped records")} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            fullWidth
            size="small"
            value={query}
            onChange={(event) => resetPage(() => setQuery(event.target.value))}
            placeholder={t("Search block hash, Tx ID, business ID, payload hash, organization, or operation")}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} /> }}
          />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => loadPage({ animateNew: false })} sx={{ whiteSpace: 'nowrap' }}>{t("Refresh")}</Button>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          <Chip size="small" color="primary" variant="outlined" label={t("{0} blocks", [pagination.totalItems])} />
          <Chip size="small" variant="outlined" label={t("{0} scoped tx", [rows.filter((tx) => tx.matches_filter !== false).length])} />
          <Chip size="small" variant="outlined" label={t("{0} shown", [blockGroups.length])} />
        </Stack>
      </Paper>

      <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 1040 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>{t("Block")}</strong></TableCell>
              <TableCell><strong>{t("Time")}</strong></TableCell>
              <TableCell><strong>{t("Tx Count")}</strong></TableCell>
              <TableCell><strong>{ui(counterpartyColumnLabel(config))}</strong></TableCell>
              <TableCell><strong>{t("Business ID")}</strong></TableCell>
              <TableCell><strong>{t("Operation")}</strong></TableCell>
              <TableCell><strong>{t("Block Hash")}</strong></TableCell>
              <TableCell><strong>{t("Status")}</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={26} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t("Syncing block data.")}</Typography>
                </TableCell>
              </TableRow>
            ) : blockGroups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>{t("No blocks match the current filters.")}</TableCell>
              </TableRow>
            ) : blockGroups.map((block) => {
              const tx = block.representative;
              return (
                <TableRow
                  hover
                  key={block.blockNumber}
                  onClick={() => setSelectedBlockNumber(block.blockNumber)}
                  sx={{ cursor: 'pointer', ...(newBlockNumbers.has(block.blockNumber) ? newBlockSx : {}) }}
                >
                  <TableCell sx={{ fontWeight: 'bold' }}>#{block.blockNumber}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTime(tx.block_created_at)}</TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography variant="body2">{block.rows.length}</Typography>
                      {block.visibleCount !== block.rows.length && <Typography variant="caption" color="text.secondary">{t("Visible")}{" "}{block.visibleCount}</Typography>}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }} title={flowOf(tx)}>{counterpartyOf(tx, config)}</TableCell>
                  <TableCell title={liveBusinessId(tx)} sx={{ fontFamily: 'monospace', maxWidth: 210, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {shortBusinessId(liveBusinessId(tx))}
                  </TableCell>
                  <TableCell><OperationBadge operation={liveOperation(tx)} /></TableCell>
                  <TableCell title={tx.block_hash || ''} sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{shortHash(tx.block_hash, 10, 8)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      <LedgerStatusChip tx={tx} />
                      <IndexStatusChip tx={tx} />
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination getItemAriaLabel={paginationLabel} aria-label={t("Pagination")} page={currentPage} count={totalPages} color="primary" onChange={(_, page) => setCurrentPage(page)} />
        </Stack>
      )}

      <PartnerBlockDetailDialog
        config={config}
        block={selectedBlock}
        onClose={() => setSelectedBlockNumber(null)}
        onSelectTx={(tx) => {
          setSelectedBlockNumber(null);
          setSelectedTx(tx);
        }}
      />
      <IntegrationTransactionDetailDialog tx={selectedTx} onClose={() => setSelectedTx(null)} />
    </Box>
  );
}
